import { MouseEvent as ReactMouseEvent } from 'react';
import { PropTypes } from 'typed/imports';
import { PureComponent, ReactEventHandler } from 'typed/components/utils';
import { addEvent, removeEvent } from 'typed/utils';
import FormattedResource from '../FormattedResource/FormattedResource';
import './WherebyVideoCall.less';

// https://docs.whereby.com/customizing-rooms/using-url-parameters
const CALL_PROPERTIES: { [key: string]: any; } = {
    audio: 'off',
    chat: 'off',
    lang: 'en',
    video: 'off'
};

interface CallProps {
    source: string;
}

class CallFrame extends PureComponent<CallProps> {
    static readonly propTypes = {
        source: PropTypes.string.isRequired
    };

    componentDidMount() {
        this.componentDidUpdate();
    }

    shouldComponentUpdate(nextProps: CallProps) {
        return this.props.source !== nextProps.source;
    }

    componentDidUpdate() {
        // Iframe hack. For some reason React claims to not support the "allow" iframe property.
        const iframe = this.refs.iframe as HTMLIFrameElement | undefined;

        if (iframe) {
            iframe.allow = 'camera;microphone;fullscreen;display-capture;autoplay';
            iframe.src = this.props.source;
        }
    }

    render() {
        return this.props.source ? <iframe ref="iframe" /> : null;
    }
}

interface Props {
    roomUrl: string;
    userName: string;
}

interface State {
    callSource: string;
    position: { top: number; left: number; } | null;
    minimized: boolean;
}

export default class WherebyVideoCall extends PureComponent<Props> {
    static readonly propTypes = {
        roomUrl: PropTypes.string.isRequired,
        userName: PropTypes.string.isRequired
    };

    state: State = {
        callSource: '',
        position: null,
        minimized: false
    };

    _currentX: number | null = null;
    _currentY: number | null = null;
    _offsetX: number | null = null;
    _offsetY: number | null = null;


    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.roomUrl) {
            let source = `${nextProps.roomUrl}?minimal&displayName=${encodeURIComponent(nextProps.userName)}`;

            for (const propertyName in CALL_PROPERTIES) {
                if (Object.prototype.hasOwnProperty.call(CALL_PROPERTIES, propertyName)) {
                    source += `&${propertyName}=${CALL_PROPERTIES[propertyName]}`;
                }
            }

            this.setState({ callSource: source });
        }
    }


    private _onToggleMinimizeClick: ReactEventHandler = () => {
        this.setState({ minimized: !this.state.minimized });
    };

    private _onDragMouseDown: ReactEventHandler<ReactMouseEvent<HTMLButtonElement>> = (event) => {
        const
            element = this.refs['video-call'] as unknown as HTMLElement,
            bounds = element.getBoundingClientRect();

        this._currentX = event.clientX;
        this._currentY = event.clientY;
        this._offsetX = bounds.left - this._currentX;
        this._offsetY = bounds.top - this._currentY;

        this._onDragMouseMove(event as unknown as any);
        addEvent(document.body, 'mousemove', this._onDragMouseMove, true);
        addEvent(document.body, 'mouseup', this._onDragMouseUp, true);
    };

    private _onDragMouseMove = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        const element = (this.refs['video-call'] || null) as unknown as HTMLElement | null;

        if (element) {
            this._currentX = event.clientX + this._offsetX!;
            this._currentY = event.clientY + this._offsetY!;

            element.style.top = `${this._currentY}px`;
            element.style.left = `${this._currentX}px`;
        }
    };

    private _onDragMouseUp = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        removeEvent(document.body, 'mousemove', this._onDragMouseMove, true);
        removeEvent(document.body, 'mouseup', this._onDragMouseUp, true);

        this.setState({ position: { top: this._currentY!, left: this._currentX! } });

        this._currentX = null;
        this._currentY = null;

        this._offsetX = null;
        this._offsetY = null;
    };


    render() {
        const { callSource, position, minimized } = this.state;

        let callPosition = {};

        if (this._currentX && this._currentY) {
            callPosition = {
                top: `${this._currentY}px`,
                left: `${this._currentX}px`
            };
        }
        else if (position && !minimized) {
            callPosition = {
                top: `${position.top}px`,
                left: `${position.left}px`
            };
        }

        return (
            <div ref="video-call" className={`whereby-video-call${minimized ? ' minimized' : ''}`} style={callPosition}>
                <div className="row header">
                    <div className="main-col">
                        <button className="inline" disabled={!minimized} onClick={this._onToggleMinimizeClick}>
                            <FormattedResource resource={{ id: 'common.videocall' }} />
                        </button>
                    </div>
                    <div className="buttons">
                        <button className={`inline fa fa-window-${!minimized ? 'minimize' : 'maximize'}`} onClick={this._onToggleMinimizeClick} />
                        <button className="inline fa fa-arrows" disabled={minimized} onMouseDown={this._onDragMouseDown} />
                    </div>
                </div>

                <div className="frame"><CallFrame source={callSource} /></div>
            </div>
        );
    }
}
